<script>
	import thumb_OVpTGuisUlU from '$lib/Components/Youtube/images/OVpTGuisUlU.jpg?w=610';

	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import arrowPointOutOfWindowImg from './images/arrow-point-out.jpg';
	import bubbleWrapImg from './images/bubble-wrap.png';
	import draftImg from './images/draft.jpg';
</script>

<Section>
	<p>
		Older windows, even some newer windows, can let cold air right into your
		home. It happens because of cracks, failed seals, and poor insulation. If
		you’re trying to fix this problem by putting bubble wrap on windows, you can
		do better. Read on for how to keep cold air from coming through windows.
	</p>
	<h2>Bubble Wrap Windows</h2>
	<p>
		Adding bubble wrap or saran wrap to your windows does help insulate them,
		but not very well. And these fixes look sloppy, especially when they have to
		be done over each year, leaving adhesive marks or chipped paint.
	</p>
	<p>
		What bubble wrap can’t do, is create dead air space between it and your
		window. That’s a big reason why double-pane windows insulate better than
		single-pane, the space between the panes. When trying to <a
			href="https://youtu.be/PZAPdV5kWIc"
			>keep cold from coming through your windows</a
		>, you want to add layers – of material AND air.
	</p>
	<Image
		src={bubbleWrapImg}
		alt="woman adding bubble wrap windows for insulation"
	/>
</Section>
<Section>
	<h2>Understand R Value vs. U Value</h2>
	<p>
		The way your house feels depends on the make-up of your house: wall and
		attic insulation, double or single-pane windows. All of these have an R
		value and a U value that together make the mean radiant room temperature.
	</p>
	<p>
		R value measures heat resistance and U value measures heat transference. A
		low U value (transfer of heat) and high R value (resistance to heat
		transfer) is ideal for high surface temperature. Together, these give us the
		combined effectiveness of the materials in insulating a room and our
		perceived temperature.
	</p>
	<p>
		Mean radiant room temperature expresses the influence of all surface
		temperatures on comfort. That’s why someone sitting next to a window can be
		cold while someone across the room is fine. No matter what the thermostat
		says, if your windows aren’t insulated, you will have a low perceived
		temperature and comfort level.
	</p>
	<div
		class="
    flex w-full flex-col justify-around gap-8
    md:flex-row
  "
	>
		<Image src={arrowPointOutOfWindowImg} class="max-w-32" />
		<Image src={draftImg} class="max-w-32" />
	</div>
</Section>
<Section>
	<h2>How to Keep Cold Air From Coming Through Windows with Inserts</h2>
	<p>
		Give up the bubble wrap. To really keep cold air from coming through your
		windows and increase the perceived temperature, you need to increase
		insulation. You can add another layer of insulating material and air with
		window inserts.
	</p>
	<p>
		<a href="/solutions/insulating-drafty-windows">Indow window inserts</a> are made
		with acrylic, which insulates better than glass. They use compression tubing to
		create a seal around the window frame. This adds an air pocket between the insert
		and the window for more insulation. Because the insert is on the inside of the
		house, it raises the surface temperature.
	</p>
	<p>
		Just one insert can have a huge impact on the mean radiant room temperature. <a
			href="/how-to/get-indow-inserts">Start with the coldest window</a
		>
	</p>
	<Youtube
		posterImageSrc={thumb_OVpTGuisUlU}
		src="https://www.youtube.com/embed/OVpTGuisUlU"
		title="How to Insulate Windows | Indow"
	/>
</Section>

<GetAFreeEstimate title="Let Us Help You" />
<Reviews />
